import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import StandardNavigation from "../../../components/navigation/StandardNavigation";
import Animated, {
  useSharedValue,
  FadeInDown,
  LinearTransition,
  useAnimatedRef,
} from "react-native-reanimated";
import React, { useEffect, useState, useCallback } from "react";
import StatsCard from "../../../components/stats/StatsCard";
import Bill from "../../../components/stats/Bill";
import customAxios from "../../../utils/customAxios";
import i18n from "../../../locales/i18n";
import { Toast } from "react-native-toast-notifications";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { appActions } from "../../../store/slices/appSlice";
import { useFocusEffect } from "expo-router";
import WebDashboardScreen from "../../../components/web/WebDashboardScreen";
import { BarChart } from "react-native-gifted-charts";
import InlineCalendarPicker from "../../../components/shared/InlineCalendarPicker";
import {
  StatsData,
  DateOption,
  fetchStatisticIntervalle,
  getDateRangeForFilter,
} from "../../../utils/statisticApi";

interface ChartDataItem {
  value: number;
  label: string;
  frontColor: string;
}

const Statistique = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state: RootState) => state.app);

  const [selectedItem, setSelectedItem] = useState<DateOption>({
    label: i18n.t("today"),
    value: "today",
  });

  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch]),
  );

  const [initialLoading, setInitialLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [billsLoading, setBillsLoading] = useState(false);

  const [showCalendar, setShowCalendar] = useState(false);
  const [customStart, setCustomStart] = useState<Date>(new Date());
  const [customEnd, setCustomEnd] = useState<Date>(new Date());

  const flatListRef = useAnimatedRef<FlatList>();
  const selectedIndex = useSharedValue(0);

  const [stats, setStats] = useState<StatsData>({
    totalselles: 0,
    total: 0,
    totalprofit: 0,
    totalcapital: 0,
  });

  const [dateOptions] = useState<DateOption[]>([
    { label: i18n.t("tout"), value: "all" },
    { label: i18n.t("today"), value: "today" },
    { label: i18n.t("yesterday"), value: "yesterday" },
    { label: i18n.t("mounth"), value: "month" },
    { label: i18n.t("year"), value: "year" },
    { label: i18n.t("custom"), value: "custom" },
  ]);

  const [bills, setBills] = useState<any[]>([]);
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const buildChartData = (factures: any[]) => {
    const grouped: Record<string, number> = {};
    factures.forEach((f: any) => {
      const d = new Date(f.created_at);
      const key = `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
      if (!grouped[key]) grouped[key] = 0;
      grouped[key] += (f.capital || 0) + (f.profit || 0);
    });

    const sorted = Object.entries(grouped)
      .sort(([a], [b]) => {
        const [da, ma] = a.split("/").map(Number);
        const [db, mb] = b.split("/").map(Number);
        return ma !== mb ? ma - mb : da - db;
      })
      .slice(-10);

    const mappedChartData: ChartDataItem[] = sorted.map(([name, total]) => ({
      value: total,
      label: name,
      frontColor: "#6D7BDB",
    }));

    setChartData(mappedChartData);
  };

  // Fetch stats using /statistic_intervalle (always, no /statistic fallback)
  const fetchStats = useCallback(
    async (dayValue: string = "all", isInitial: boolean = false) => {
      if (isInitial) setInitialLoading(true);
      else {
        setStatsLoading(true);
        setBillsLoading(true);
      }

      const minLoadingTime = 300;
      const startTime = Date.now();

      try {
        const dates = getDateRangeForFilter(dayValue, customStart, customEnd);
        const data = await fetchStatisticIntervalle(dates.start, dates.end);

        const factures = Array.isArray(data.factures) ? data.factures : [];
        setStats({
          totalselles: data.totalselles || 0,
          total: data.total || 0,
          totalprofit: data.totalprofit || 0,
          totalcapital: data.totalcapital || 0,
        });
        setBills(factures);
        buildChartData(factures);
      } catch (error: any) {
        if (error?.response?.status === 404) {
          setStats({
            totalselles: 0,
            total: 0,
            totalprofit: 0,
            totalcapital: 0,
          });
          setBills([]);
          setChartData([]);
        } else {
          Toast.show(i18n.t("findBillsError"), { type: "danger" });
        }
      } finally {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        setTimeout(() => {
          setInitialLoading(false);
          setStatsLoading(false);
          setBillsLoading(false);
        }, remainingTime);
      }
    },
    [customStart, customEnd],
  );

  const handleChangeDate = useCallback(
    async (day: DateOption) => {
      if (statsLoading || billsLoading) return;
      setSelectedItem(day);

      const index = dateOptions.findIndex((item) => item.value === day.value);
      selectedIndex.value = index;
      flatListRef.current?.scrollToOffset({
        offset: index * 100 - 150,
        animated: true,
      });

      if (day.value === "custom") {
        setShowCalendar(true);
      } else {
        await fetchStats(day.value, false);
      }
    },
    [
      dateOptions,
      selectedIndex,
      flatListRef,
      fetchStats,
      statsLoading,
      billsLoading,
    ],
  );

  const handleCalendarApply = useCallback(
    (start: Date, end: Date) => {
      setCustomStart(start);
      setCustomEnd(end);
      setShowCalendar(false);
      setTimeout(() => {
        fetchStats("custom", false);
      }, 100);
    },
    [fetchStats],
  );

  useEffect(() => {
    if (selectedItem.value === "custom" && !showCalendar) {
      fetchStats("custom", false);
    }
  }, [customStart, customEnd]);

  const handleDelete = useCallback(
    async (id: number) => {
      setDeletingId(id);
      try {
        await customAxios.delete(`/statistic/${id}`, { withCredentials: true });
        setBills((prev) => prev.filter((bill) => bill.id_facture !== id));
        await fetchStats(selectedItem.value, false);
        Toast.show(i18n.t("deleteSuccess"), { type: "success" });
      } catch (error) {
        Toast.show(i18n.t("deleteError"), { type: "danger" });
      } finally {
        setDeletingId(null);
      }
    },
    [selectedItem.value, fetchStats],
  );

  useEffect(() => {
    const initializeScroll = () => {
      const index = dateOptions.findIndex(
        (item) => item.value === selectedItem.value,
      );
      selectedIndex.value = index;
      flatListRef.current?.scrollToOffset({
        offset: index * 100 - 150,
        animated: false,
      });
    };
    const timer = setTimeout(initializeScroll, 50);
    return () => clearTimeout(timer);
  }, [dateOptions, selectedItem.value]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchStats("today", true);
    }, 100);
    return () => clearTimeout(timer);
  }, [fetchStats]);

  if (Platform.OS === "web") {
    return <WebDashboardScreen />;
  }

  if (initialLoading) {
    return (
      <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
        <StandardNavigation
          title={i18n.t("statistics")}
          showBackButton={true}
          showMenuButton={true}
          showLogo={true}
        />
        <View className="flex-1 justify-center items-center">
          <ActivityIndicator size="large" color="#279F9E" />
          <Text className="mt-4 text-lg text-gray-600">
            {i18n.t("loading")}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
      <StandardNavigation
        title={i18n.t("statistics")}
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
      />

      {/* Calendar Picker Modal */}
      <InlineCalendarPicker
        visible={showCalendar}
        onClose={() => setShowCalendar(false)}
        onApply={handleCalendarApply}
      />

      <ScrollView showsVerticalScrollIndicator={false}>
        <View className="flex-1">
          {/* Date Selection */}
          <View className="sticky z-30 py-2 px-8 my-2 top-0">
            <View className="shadow bg-white rounded-full">
              <Animated.FlatList
                data={dateOptions}
                ref={flatListRef}
                getItemLayout={(_, index) => ({
                  length: 100,
                  offset: 100 * index,
                  index,
                })}
                keyExtractor={(item) => item.value}
                horizontal={true}
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    key={item.value}
                    onPress={() => handleChangeDate(item)}
                    style={{
                      borderRadius: 9999,
                      opacity: statsLoading ? 0.6 : 1,
                    }}
                    disabled={statsLoading}
                  >
                    {selectedItem.value === item.value ? (
                      <LinearGradient
                        colors={[
                          "rgba(19, 121, 162, 1)",
                          "rgba(5, 150, 208, 1)",
                        ]}
                        style={{
                          marginHorizontal: 12,
                          paddingHorizontal: 16,
                          paddingVertical: 4,
                          borderRadius: 9999,
                        }}
                      >
                        <Text className="text-white mx-2 text-2xl px-3 py-1 rounded-full">
                          {item.label}
                        </Text>
                      </LinearGradient>
                    ) : (
                      <View
                        style={{
                          marginHorizontal: 12,
                          paddingHorizontal: 16,
                          paddingVertical: 4,
                          borderRadius: 9999,
                          backgroundColor: "white",
                        }}
                      >
                        <Text className="text-gray-700 mx-2 text-2xl px-3 py-1 rounded-full">
                          {item.label}
                        </Text>
                      </View>
                    )}
                  </TouchableOpacity>
                )}
              />
            </View>
          </View>

          {/* Custom date range display */}
          {selectedItem.value === "custom" && !statsLoading && (
            <Animated.View
              entering={FadeInDown.duration(400)}
              className="px-8 pb-2"
            >
              <TouchableOpacity
                onPress={() => setShowCalendar(true)}
                className="flex-row items-center justify-center gap-2 bg-white rounded-xl py-3 px-4 shadow-sm"
              >
                <MaterialIcons name="date-range" size={20} color="#1379A2" />
                <Text className="text-primaryV2 font-semibold text-base">
                  {customStart.toLocaleDateString()} →{" "}
                  {customEnd.toLocaleDateString()}
                </Text>
                <MaterialIcons name="edit" size={16} color="#1379A2" />
              </TouchableOpacity>
            </Animated.View>
          )}

          {/* Stats Cards */}
          <View className="p-4">
            <Animated.View
              entering={FadeInDown.duration(600).delay(100)}
              className="flex flex-row gap-4 justify-center"
            >
              <StatsCard
                titel={i18n.t("total")}
                number={
                  (statsLoading ? (
                    <ActivityIndicator size="small" color="#279F9E" />
                  ) : (
                    `${stats.total}${i18n.t("currency")}`
                  )) as any
                }
              />
              <StatsCard
                titel={i18n.t("profit")}
                number={
                  (statsLoading ? (
                    <ActivityIndicator size="small" color="#279F9E" />
                  ) : (
                    `${stats.totalprofit}${i18n.t("currency")}`
                  )) as any
                }
              />
            </Animated.View>
            <Animated.View
              entering={FadeInDown.duration(600).delay(200)}
              className="flex flex-row gap-4 justify-center mt-4"
            >
              <StatsCard
                titel={i18n.t("nbrofsell")}
                number={
                  (statsLoading ? (
                    <ActivityIndicator size="small" color="#279F9E" />
                  ) : (
                    stats.totalselles.toString()
                  )) as any
                }
              />
              <StatsCard
                titel={i18n.t("budget")}
                number={
                  (statsLoading ? (
                    <ActivityIndicator size="small" color="#279F9E" />
                  ) : (
                    `${stats.totalcapital}${i18n.t("currency")}`
                  )) as any
                }
              />
            </Animated.View>
          </View>

          {/* Bar Chart Section */}
          {!statsLoading && chartData.length > 0 && (
            <Animated.View
              entering={FadeInDown.duration(600).delay(250)}
              className="px-4 pb-4"
            >
              <View className="p-4 rounded-xl bg-white shadow-sm flex items-center justify-center">
                <Text className="text-xl text-primaryV2 font-bold mb-4 self-start">
                  {i18n.t("revenue")}
                </Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <BarChart
                    data={chartData}
                    barWidth={18}
                    spacing={24}
                    roundedTop
                    xAxisThickness={0}
                    yAxisThickness={0}
                    hideYAxisText
                    noOfSections={4}
                    frontColor="lightgray"
                    isAnimated
                  />
                </ScrollView>
              </View>
            </Animated.View>
          )}

          {/* Bills Section */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(300)}
            className="px-4 pb-4"
          >
            <View className="p-4 mb-4 flex rounded-2xl bg-white shadow-sm">
              <Text
                className={`${locale === "fr" ? "text-left" : "text-right"} text-3xl mb-2 text-primaryV2 font-bold`}
              >
                {i18n.t("bills")}
              </Text>
              {billsLoading && (
                <View className="flex justify-center items-center py-8">
                  <ActivityIndicator size="large" color="#279F9E" />
                  <Text className="mt-2 text-gray-600 text-center">
                    {i18n.t("loading")}
                  </Text>
                </View>
              )}
              {!billsLoading && bills.length > 0 && (
                <>
                  {bills.map((item, index) => (
                    <Animated.View
                      key={`animated-bill-${item.id_facture || index}`}
                      entering={FadeInDown.duration(400).delay(
                        100 + index * 50,
                      )}
                      layout={LinearTransition}
                    >
                      <Bill
                        info={item as any}
                        buttonSpinnerDelete={deletingId === item.id_facture}
                        handleDelete={(id) => handleDelete(Number(id))}
                      />
                    </Animated.View>
                  ))}
                </>
              )}
              {!billsLoading && bills.length === 0 && (
                <View className="py-8 flex justify-center items-center">
                  <MaterialIcons name="receipt-long" size={48} color="#ccc" />
                  <Text className="text-center text-lg text-gray-500 mt-2">
                    {i18n.t("nobills")}
                  </Text>
                </View>
              )}
            </View>
          </Animated.View>
        </View>
      </ScrollView>
    </View>
  );
};

export default Statistique;
